import { useState, useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { CertificateFormData } from '../../types';
import { updatePatient, saveCertificate } from '../../services/api/apis';

const defaultFormData: CertificateFormData = {
  patientName: '',
  drName: '',
  specialization: '',
  regNo: '',
  issueDate: new Date().toISOString().split('T')[0],
  docId: '',
  certificateType: '',
  to: 'To',
  organisation: '',
  signature: '',
  guardianName: '',
  age: '',
  gender: '',
  identificationMark: '',
  address: '',
  examDate: new Date().toISOString().split('T')[0],
  examTime: new Date().toTimeString().slice(0, 5),
  diagnosis: '',
  noOfDays: '',
  fromDate: '',
  caretakerFullName: '',
  caretakerAge: '',
  caretakerRelation: '',
  fitStamp: '',
  content: '',
  validity: '',
  illnessDetails: '',
  additionalDetails: '',
  he_she: '',
  his_her: '',
  him_her: '',
  patientTitle: '',
};

type ValidationErrors = Partial<Record<keyof CertificateFormData, string>>;

function getRequiredFields(certificateType: string): (keyof CertificateFormData)[] {
  const base: (keyof CertificateFormData)[] = [
    'patientName', 'drName', 'regNo', 'issueDate', 'age', 'gender', 'diagnosis',
  ];
  switch (certificateType) {
    case 'SICK LEAVE CERTIFICATE':
    case 'WORK FROM HOME CERTIFICATE':
      return [...base, 'noOfDays', 'fromDate'];
    case 'RECOVERY CERTIFICATE':
      return [...base, 'fromDate'];
    case 'CARETAKER CERTIFICATE':
      return [...base, 'caretakerFullName', 'caretakerAge', 'caretakerRelation'];
    case 'UNFIT TO TRAVEL CERTIFICATE':
      return [...base, 'noOfDays', 'fromDate'];
    default:
      return base;
  }
}

export interface UseCertificateFormOptions {
  patientDocId?: string;
  initialData?: Partial<CertificateFormData>;
  onSubmitSuccess?: () => void;
}

export interface UseCertificateFormReturn {
  formData: CertificateFormData;
  errors: ValidationErrors;
  isDirty: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  setField: <K extends keyof CertificateFormData>(field: K, value: CertificateFormData[K]) => void;
  validate: () => boolean;
  reset: () => void;
  submit: () => Promise<void>;
}

export function useCertificateForm(options: UseCertificateFormOptions = {}): UseCertificateFormReturn {
  const { patientDocId, initialData, onSubmitSuccess } = options;

  const buildInitial = (): CertificateFormData => ({ ...defaultFormData, ...initialData });

  const [formData, setFormData] = useState<CertificateFormData>(buildInitial);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const baselineRef = useRef(JSON.stringify(buildInitial()));
  const isDirty = JSON.stringify(formData) !== baselineRef.current;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => {
        if (!prev[name as keyof CertificateFormData]) return prev;
        const next = { ...prev };
        delete next[name as keyof CertificateFormData];
        return next;
      });
    },
    []
  );

  const setField = useCallback(
    <K extends keyof CertificateFormData>(field: K, value: CertificateFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  const validate = useCallback((): boolean => {
    const required = getRequiredFields(formData.certificateType);
    const newErrors: ValidationErrors = {};

    for (const field of required) {
      const val = formData[field];
      if (!val || String(val).trim() === '') {
        newErrors[field] = 'This field is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const reset = useCallback(() => {
    const fresh = { ...defaultFormData, ...initialData };
    setFormData(fresh);
    setErrors({});
    setSubmitError(null);
    baselineRef.current = JSON.stringify(fresh);
  }, [initialData]);

  const submit = useCallback(async () => {
    if (!validate()) return;
    if (!patientDocId) {
      setSubmitError('No patient selected');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Save patient metadata fields back to the patient record
      await updatePatient(patientDocId, {
        name: formData.patientName,
        age: formData.age,
        gender: formData.gender || undefined,
        diagnosis: formData.diagnosis,
        no_of_days: formData.noOfDays || undefined,
        from_date: formData.fromDate || undefined,
        full_address: formData.address || undefined,
        organisation: formData.organisation || undefined,
        additional_details: formData.additionalDetails || undefined,
        detail_of_illness: formData.illnessDetails || undefined,
        id_mark: formData.identificationMark || undefined,
        certificate_type: formData.certificateType,
      });

      // Save the full certificate form (drName, regNo, specialization,
      // issueDate, signature, content, validity, etc.) to its own item
      // so it can be reloaded and edited later.
      await saveCertificate(patientDocId, formData);

      baselineRef.current = JSON.stringify(formData);
      onSubmitSuccess?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, patientDocId, formData, onSubmitSuccess]);

  return {
    formData,
    errors,
    isDirty,
    isSubmitting,
    submitError,
    handleChange,
    setField,
    validate,
    reset,
    submit,
  };
}

export default useCertificateForm;
